import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { FaTimes } from "react-icons/fa";
import Button from "../Button/Button";
import Tooltip from "../Tooltip/Tooltip";
import clsx from "clsx"; // Use o pacote clsx

export type Chip = {
  id: string;
  label: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  tooltip?: string;
  "data-testid"?: string;
};

export interface ChipsProps {
  items: Chip[];
  onDelete?: (id: string) => void;
  onClearAll?: () => void;
  theme?: "light" | "dark";
  error?: boolean;
  errorMessage?: string;
  successMessage?: string;
  helperText?: string;
  className?: string;
  "data-testid"?: string;
  containerProps?: React.HTMLAttributes<HTMLDivElement> & { "data-testid"?: string };
  tabIndex?: number;
  autoFocus?: boolean;
  children?: React.ReactNode;
  renderChip?: (chip: Chip) => React.ReactNode; // Novo prop
  maxVisible?: number; // Novo prop
}

const ChipItem = React.memo(
  ({
    chip,
    idx,
    isFocused,
    focusedChip,
    setFocusedChip,
    onDelete,
    theme,
    itemsLength,
    className,
  }: {
    chip: Chip;
    idx: number;
    isFocused: boolean;
    focusedChip: string | null;
    setFocusedChip: (id: string | null) => void;
    onDelete?: (id: string) => void;
    theme: "light" | "dark";
    itemsLength: number;
    className?: string;
  }) => {
    const isTabbable =
      focusedChip === chip.id ||
      (isFocused && focusedChip === null && idx === 0);
    return (
      <span
        className={clsx(
          // Altura 32dp, borda arredondada 8dp, sombra leve
          "flex items-center h-8 rounded-lg border transition-all duration-200 outline-none relative shadow-sm",
          // Padding horizontal: 4 (16px) sem ícone, 2 (8px) com ícone
          chip.leftIcon || chip.rightIcon
            ? "pl-2 pr-2"
            : "pl-4 pr-4",
          // Espaço entre elementos: 2 (8px)
          "gap-2",
          // Cores do tema
          theme === "dark"
            ? "bg-zinc-900 text-zinc-100 border-zinc-700"
            : "bg-[#F3F2F1] text-[#201F1E] border-[#E1DFDD]",
          // Foco
          isTabbable && "border-[#0078D4] ring-2 ring-[#C7E0F4]",
          "font-grotesk",
          className
        )}
        role="listitem"
        tabIndex={isTabbable ? 0 : -1}
        aria-label={chip.label}
        data-testid={chip["data-testid"] || `chip-${chip.id}`}
        onFocus={() => setFocusedChip(chip.id)}
        onBlur={() => setFocusedChip(null)}
        onKeyDown={e => {
          if ((e.key === "Delete" || e.key === "Backspace") && onDelete) {
            e.preventDefault();
            onDelete(chip.id);
          }
          if (e.key === "ArrowRight" && idx < itemsLength - 1) {
            e.preventDefault();
            setFocusedChip?.(null);
          }
          if (e.key === "ArrowLeft" && idx > 0) {
            e.preventDefault();
            setFocusedChip?.(null);
          }
        }}
        style={{
          minHeight: 32, // 32dp
          borderRadius: 8, // 8dp
        }}
      >
        {chip.leftIcon && (
          <span className="flex items-center" style={{ fontSize: 18, marginRight: 8 }}>
            {chip.leftIcon}
          </span>
        )}
        <span
          className="font-medium font-inter text-center"
          style={{
            alignItems: "center",
            justifyContent: "flex-start",
            display: "flex",
            height: "100%",
          }}
        >
          {chip.label}
        </span>
        {chip.tooltip && (
          <Tooltip
            tooltip={chip.tooltip}
            placement="top"
            tabIndex={0}
            style={{ marginLeft: 8 }}
            data-testid={`chip-tooltip-${chip.id}`}
          />
        )}
        {chip.rightIcon && (
          <span className="flex items-center" style={{ fontSize: 18, marginLeft: 8 }}>
            {chip.rightIcon}
          </span>
        )}
        {onDelete && (
          <Button
            as="button"
            className={clsx(
              "ml-0.5 p-0 rounded-full transition-colors text-[#605E5C] hover:bg-[#E5F1FB] hover:text-[#0078D4] focus:bg-[#E5F1FB] focus:text-[#0078D4]",
              "focus:ring-2 focus:ring-[#C7E0F4]",
              "chip-delete"
            )}
            title="Remover"
            ariaLabel={`Remover ${chip.label}`}
            onClick={() => onDelete(chip.id)}
            size="tiny"
            theme={theme}
            buttonClassName="chip-delete"
            tabIndex={0}
            data-testid={`chip-delete-${chip.id}`}
            icon={<FaTimes style={{ fontSize: 16 }} />}
            transparent
            noBorder
          />
        )}
      </span>
    );
  }
);

ChipItem.displayName = "ChipItem";

const Chips = forwardRef<HTMLDivElement, ChipsProps>((props, ref) => {
  const {
    items,
    onDelete,
    onClearAll,
    theme = "light",
    error,
    errorMessage,
    successMessage,
    helperText,
    className,
    "data-testid": dataTestId,
    containerProps,
    tabIndex,
    autoFocus,
    children,
    renderChip,
    maxVisible,
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  // const [tooltipOpen, setTooltipOpen] = useState<string | null>(null);
  const [focusedChip, setFocusedChip] = useState<string | null>(null);
  // Estado para animação de saída
  const [removingIds, setRemovingIds] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

  useEffect(() => {
    if (autoFocus && containerRef.current) {
      containerRef.current.focus();
    }
  }, [autoFocus]);

  // Atualiza o foco após remoção de chip
  useEffect(() => {
    if (focusedChip && !items.some(chip => chip.id === focusedChip)) {
      // Tenta focar o próximo chip, ou o anterior, ou nenhum
      const idx = items.findIndex(chip => chip.id === focusedChip);
      let nextChipId: string | null = null;
      if (items.length > 0) {
        if (idx >= 0 && idx < items.length) {
          nextChipId = items[idx]?.id;
        } else if (idx - 1 >= 0) {
          nextChipId = items[idx - 1]?.id;
        } else {
          nextChipId = items[0]?.id;
        }
      }
      setFocusedChip(nextChipId ?? null);
    }
  }, [items, focusedChip]);

  // Handler animado para remoção
  const handleDelete = useCallback(
    (id: string) => {
      setRemovingIds(ids => [...ids, id]);
      setTimeout(() => {
        onDelete?.(id);
        setRemovingIds(ids => ids.filter(rid => rid !== id));
      }, 250); // tempo igual ao transition do CSS
    },
    [onDelete]
  );

  const handleClearAll = useCallback(() => {
    onClearAll?.();
  }, [onClearAll]);


  const containerClass = clsx(
    "flex flex-wrap items-center gap-2 mb-4 min-h-[36px] outline-none transition-shadow max-w-full overflow-x-auto shadow-sm",
    "bg-[#F3F2F1]",
    error
      ? "shadow-[0_0_0_2px_rgba(229,57,53,0.33)]"
      : successMessage
      ? "shadow-[0_0_0_2px_rgba(56,142,60,0.33)]"
      : "",
    "font-grotesk",
    className,
    containerProps?.className
  );

  const finalTestId =
    dataTestId || (containerProps && containerProps["data-testid"]) || "chips";

  // Lógica para maxVisible
  const visibleChips = typeof maxVisible === "number" && maxVisible > 0
    ? items.slice(0, maxVisible)
    : items;
  const hiddenCount = typeof maxVisible === "number" && maxVisible > 0
    ? Math.max(items.length - maxVisible, 0)
    : 0;

  // Memoize chips rendering
  const chipsList = useMemo(
    () =>
      visibleChips.map((chip, idx) => {
        const isExiting = removingIds.includes(chip.id);
        if (renderChip) {
          return (
            <span
              key={chip.id}
              className={clsx(isExiting && "chip-exit")}
              style={{ display: "inline-flex" }}
              role="listitem" // <-- Adicione este papel
            >
              {renderChip(chip)}
            </span>
          );
        }
        return (
          <ChipItem
            key={chip.id}
            chip={chip}
            idx={idx}
            isFocused={isFocused}
            focusedChip={focusedChip}
            setFocusedChip={setFocusedChip}
            onDelete={handleDelete}
            theme={theme}
            itemsLength={visibleChips.length}
          />
        );
      }),
    [
      visibleChips,
      renderChip,
      isFocused,
      focusedChip,
      handleDelete,
      theme,
      removingIds,
    ]
  );
  return (
    <>
      <div
        {...containerProps}
        ref={containerRef}
        className={containerClass}
        tabIndex={tabIndex}
        data-testid={finalTestId}
        role="list"
        aria-label="Lista de chips"
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          setFocusedChip(null);
        }}
      >
        {chipsList}
      </div>
      {/* Elementos fora do role="list" para acessibilidade */}
      {hiddenCount > 0 && (
        <span
          className={clsx(
            "flex items-center h-8 rounded-lg border transition-all duration-200 outline-none relative shadow-sm",
            "pl-4 pr-4 gap-2 font-grotesk font-medium",
            theme === "dark"
              ? "bg-zinc-900 text-zinc-100 border-zinc-700"
              : "bg-[#F3F2F1] text-[#201F1E] border-[#E1DFDD]"
          )}
          tabIndex={-1}
          aria-hidden="true"
          style={{
            minHeight: 32,
            borderRadius: 8,
          }}
        >
          +{hiddenCount} mais...
        </span>
      )}
      {items.length > 0 && onClearAll && (
        <Button
          title="Limpar todos"
          as="button"
          className={clsx(
            "flex items-center h-8 rounded-lg border transition-all duration-200 outline-none relative shadow-sm",
            "pl-4 pr-4 gap-2 font-grotesk font-medium",
            theme === "dark"
              ? "bg-zinc-900 text-zinc-100 border-zinc-700"
              : "bg-[#F3F2F1] text-[#201F1E] border-[#E1DFDD]",
            "hover:bg-[#E5F1FB] hover:text-[#0078D4] focus:bg-[#E5F1FB] focus:text-[#0078D4]",
            "focus:ring-2 focus:ring-[#C7E0F4]",
            "chip-clear-all"
          )}
          ariaLabel="Limpar todos os chips"
          onClick={handleClearAll}
          // size="tiny" // Remova ou troque para "small"
          theme={theme}
          buttonClassName="chip-clear-all"
          data-testid="chip-clear-all"
          transparent
          noBorder
          style={{
            minHeight: 32,
            borderRadius: 8,
          }}
        />
      )}
      {children}
      {/* Feedbacks fora do role="list" */}
      {helperText && !error && !successMessage && (
        <p
          className={clsx(
            "text-[0.95em] ml-2 mt-0.5",
            theme === "dark" ? "text-zinc-300" : "text-zinc-500"
          )}
          aria-live="polite"
          aria-atomic="true"
          role="status"
        >
          {helperText}
        </p>
      )}
      {error && (
        <p
          className="text-[0.95em] text-red-600 ml-2 mt-0.5"
          aria-live="assertive"
          aria-atomic="true"
          role="alert"
        >
          {errorMessage || "Erro ao exibir chips"}
        </p>
      )}
      {successMessage && (
        <p
          className="text-[0.95em] text-green-700 ml-2 mt-0.5 flex items-center gap-1"
          aria-live="polite"
          aria-atomic="true"
          role="status"
        >
          {successMessage}
        </p>
      )}
    </>
  );
});

Chips.displayName = "Chips";
export default Chips;

/**
 * Chips - Componente de lista de chips interativos e acessíveis.
 *
 * Permite exibir, remover e customizar chips, com suporte a acessibilidade, animação de remoção,
 * responsividade, renderização customizada e feedbacks para leitores de tela.
 *
 * @component
 * @example
 * // Uso básico
 * <Chips
 *   items={[
 *     { id: '1', label: 'Tag 1' },
 *     { id: '2', label: 'Tag 2', tooltip: 'Mais informações' }
 *   ]}
 *   onDelete={id => console.log('Remover', id)}
 * />
 *
 * @example
 * // Com renderização customizada e maxVisible
 * <Chips
 *   items={[
 *     { id: '1', label: 'VIP', leftIcon: <StarIcon /> },
 *     { id: '2', label: 'Meia', rightIcon: <FaSmile /> },
 *     { id: '3', label: 'Cortesia' }
 *   ]}
 *   maxVisible={2}
 *   renderChip={chip => (
 *     <div style={{ background: '#eee', borderRadius: 8, padding: 4 }}>
 *       {chip.label}
 *     </div>
 *   )}
 * />
 *
 * @example
 * // Com feedbacks e botão "Limpar todos"
 * <Chips
 *   items={chips}
 *   onDelete={handleDelete}
 *   onClearAll={handleClearAll}
 *   helperText="Selecione até 3 categorias"
 *   error={hasError}
 *   errorMessage="Selecione pelo menos um chip"
 *   successMessage="Chips salvos com sucesso!"
 * />
 *
 * @param {Object}   props
 * @param {Chip[]}   props.items                Lista de chips a serem exibidos.
 * @param {function} [props.onDelete]           Função chamada ao remover um chip (recebe id).
 * @param {function} [props.onClearAll]         Função chamada ao clicar em "Limpar todos".
 * @param {"light"|"dark"} [props.theme]        Tema visual do componente.
 * @param {boolean}  [props.error]              Indica estado de erro.
 * @param {string}   [props.errorMessage]       Mensagem de erro exibida.
 * @param {string}   [props.successMessage]     Mensagem de sucesso exibida.
 * @param {string}   [props.helperText]         Texto de ajuda exibido abaixo dos chips.
 * @param {string}   [props.className]          Classe CSS adicional para o container.
 * @param {string}   [props.data-testid]        Test id para o container.
 * @param {Object}   [props.containerProps]     Props extras para o container.
 * @param {number}   [props.tabIndex]           tabIndex do container.
 * @param {boolean}  [props.autoFocus]          Se true, foca o container ao montar.
 * @param {ReactNode} [props.children]          Elementos filhos adicionais.
 * @param {function} [props.renderChip]         Função para renderização customizada de cada chip.
 * @param {number}   [props.maxVisible]         Máximo de chips visíveis antes de mostrar "+X mais...".
 *
 * @returns {JSX.Element}
 */